import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { activateRole, fetchContext, nextStep, StaffContext } from '../lib/staffContext';
import { AuthFrame } from '../ui/AuthFrame';
import { ContextError } from '../ui/ContextError';
import { roleDescription, roleLabel } from '../ui/roles';
import { useI18n } from '../i18n';

// Role chooser: shown after sign-in to a person who holds more than one role,
// and reachable from "Switch role" in the staff frame. Picking a role makes it
// the ONE active role for this sign-in (set_active_role, logged); the database
// then refuses actions that belong to the other roles until they switch back.
export function ChooseRole() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [ctx, setCtx] = useState<StaffContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetchContext().then((res) => {
      if (res.kind === 'signed_out') navigate('/teacher/login', { replace: true });
      else if (res.kind === 'no_access')
        supabase.auth.signOut().finally(() => navigate('/teacher/login?reason=no_access', { replace: true }));
      else if (res.kind === 'unavailable') setError(res.message);
      else if (res.ctx.must_set_password) navigate('/set-password', { replace: true });
      else if (res.ctx.mfa_required) navigate('/mfa', { replace: true });
      else if (res.ctx.roles.length === 0) navigate('/no-roles', { replace: true });
      else setCtx(res.ctx);
    });
  }, [navigate]);

  async function choose(grantId: string) {
    setBusy(grantId);
    setFailed(false);
    const res = await activateRole(grantId);
    setBusy(null);
    if (res.kind !== 'ok') {
      setFailed(true);
      return;
    }
    navigate(nextStep(res.ctx));
  }

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/teacher/login');
  }

  if (error) return <ContextError message={error} />;
  if (!ctx) return <main className="page-loading" role="status">{t.loading}</main>;

  return (
    <AuthFrame title={t['choose.title']} subtitle={t['choose.intro'](ctx.display_name)}>
      {ctx.is_test && <p className="notice">{t['choose.testNotice']}</p>}
      <ul className="role-list">
        {ctx.roles.map((r) => {
          const current = r.grant_id === ctx.active_grant_id;
          return (
            <li key={r.grant_id}>
              <button
                type="button"
                className={`role-option role-${r.role}${current ? ' current' : ''}`}
                onClick={() => choose(r.grant_id)}
                disabled={busy !== null}
                aria-describedby={`desc-${r.grant_id}`}
              >
                <span className="role-option-name">
                  {roleLabel(t, r.role)}
                  {current && <span className="role-option-current"> · {t['choose.current']}</span>}
                </span>
                <span className="role-option-scope">
                  {r.school_name ? `${r.school_name} · ${r.school_code ?? ''}` : r.municipality_name ?? t['ctx.allSchools']}
                </span>
                <span className="role-option-desc" id={`desc-${r.grant_id}`}>
                  {roleDescription(t, r.role)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {failed && (
        <p className="error-text" role="alert">
          {t['choose.error']}
        </p>
      )}
      <button type="button" className="link-btn" onClick={signOut}>
        {t.signOut}
      </button>
    </AuthFrame>
  );
}
